import { GalleryItemLike, GalleryLayout, GalleryRow, galleryMetrics } from './buildSections'

export interface VisibleRange {
  firstIndex: number
  lastIndex: number
}

export const flattenRows = <T extends GalleryItemLike>(
  layout: GalleryLayout<T>
): Array<GalleryRow<T>> => layout.sections.flatMap((section) => section.rows)

/** last row with top <= y */
const rowAt = <T extends GalleryItemLike>(
  rows: Array<GalleryRow<T>>,
  y: number
): number => {
  let low = 0
  let high = rows.length - 1
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    if (rows[mid].top <= y) low = mid
    else high = mid - 1
  }
  return low
}

/**
 * Indices (into the flat photos array) of the rows intersecting the viewport slice
 * [localTop, localBottom] in gallery-container coordinates.
 */
export const visibleRangeForViewport = <T extends GalleryItemLike>(
  rows: Array<GalleryRow<T>>,
  localTop: number,
  localBottom: number
): VisibleRange | null => {
  if (rows.length === 0 || localBottom <= localTop) return null

  let first = rowAt(rows, localTop)
  if (rows[first].top + rows[first].height <= localTop && first < rows.length - 1) first++
  const last = rowAt(rows, localBottom)

  // rows are sorted by top but masonry rows are not monotonic in startIndex,
  // so take min/max over the visible band
  let firstIndex = Infinity
  let lastIndex = -Infinity
  for (let i = first; i <= last; i++) {
    const row = rows[i]
    firstIndex = Math.min(firstIndex, row.startIndex)
    lastIndex = Math.max(lastIndex, row.startIndex + row.tiles.length - 1)
  }
  return { firstIndex, lastIndex }
}

/**
 * y to scroll to for a jump landing at the given index: the day header when the
 * index starts its section, otherwise the containing row with header-sized breathing room.
 */
export const scrollTargetY = <T extends GalleryItemLike>(
  layout: GalleryLayout<T>,
  index: number
): number | null => {
  for (const section of layout.sections) {
    for (const row of section.rows) {
      if (index >= row.startIndex && index < row.startIndex + row.tiles.length) {
        if (section.key && index === section.startIndex) return section.top
        return Math.max(0, row.top - galleryMetrics.headerHeight)
      }
    }
  }
  return null
}
